import React, { useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { withTranslation } from 'react-i18next';
import { Grid, InputAdornment } from '@mui/material';
import { withRights } from 'providers/RightsConsumer';
import { addPropertyPrefix } from 'utils/propertyChangeAdapters';
import PJContainerWrapper from 'components/common/select/BrowserContainer/legalPartners/PJContainerWrapper';
import { IconButton, TextField, Button } from '@totalsoft/rocket-ui';
import Search from '@mui/icons-material/Search';
import { GlobalRightsContext } from 'providers/GlobalRightsContext';

export const FacilityFrameOfferAssetClientComponent = ({
  onPropertyChange,
  t,
  client,
  validationInfo,
  rights,
  onSave,
  saving,
  isDirty,
  partnerTypeCode,
  setIsPartnerFromModal,
  loadLegalPartners,
  onUniqueIdBlur,
  isClientLoaded,
  partnerTypeId
}) => {
  const isValid = validationInfo.isValid;
  const generalData = client.generalData || {};
  const generalDataValidationInfo = validationInfo.generalData || {};
  const contactData = client.contactData || {};
  const contactDataValidationInfo = validationInfo.contactData || {};
  const [isBrowserOpen, setIsBrowserOpen] = useState(false);
  const { masterPartnerSearch } = useContext(GlobalRightsContext);

  const CustomEndIcons = ({ onCustomClick }) => (
    <InputAdornment position='end'>
      <Grid container justifyContent='space-evenly'>
        <Grid item xs={4}>
          <IconButton disabled={false} onClick={onCustomClick} variant='text'>
            <Search />
          </IconButton>
        </Grid>
      </Grid>
    </InputAdornment>
  );

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={6}>
        <PJContainerWrapper
          onTextFieldChange={addPropertyPrefix('generalData', onPropertyChange)('uniqueId')}
          value={generalData.uniqueId}
          onBlur={() => onUniqueIdBlur(generalData.uniqueId)}
          iconDisabled={false}
          iconHidden={!masterPartnerSearch}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite}
          label={t(`OfferEdit.Client.${partnerTypeCode}.PersonalIdentifier`)}
          loadOptions={loadLegalPartners}
          hasCustomIcons
          isBrowserOpen={isBrowserOpen}
          customEndIcons={
            <CustomEndIcons
              onCustomClick={() => {
                setIsBrowserOpen(true);
                setIsPartnerFromModal(true);
              }}
            />
          }
          setIsBrowserOpen={setIsBrowserOpen}
          partnerTypeId={partnerTypeId}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_companyName'}
          fullWidth
          value={generalData.partnerName || ''}
          error={generalDataValidationInfo.partnerName ? true : false}
          helperText={generalDataValidationInfo.partnerName}
          name='partnerName'
          label={t('OfferEdit.Client.PJ.CompanyName')}
          onChange={addPropertyPrefix('generalData', onPropertyChange)('partnerName')}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_personName'}
          fullWidth
          value={contactData.personName || ''}
          error={contactDataValidationInfo.personName ? true : false}
          helperText={contactDataValidationInfo.personName}
          name='personName'
          label={t('OfferEdit.Client.PJ.ContactData.PersonName')}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('personName')}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_mobilePhone'}
          fullWidth
          value={contactData.phone || ''}
          error={contactDataValidationInfo.phone ? true : false}
          helperText={contactDataValidationInfo.phone}
          name='phone'
          label={t('OfferEdit.Client.MobilePhone')}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('phone')}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          id={'offerAssetClientCard_pjPartner_email'}
          fullWidth
          value={contactData.email || ''}
          error={contactDataValidationInfo.email ? true : false}
          helperText={contactDataValidationInfo.email}
          name='email'
          label={t('OfferEdit.Client.Email')}
          onChange={addPropertyPrefix('contactData', onPropertyChange)('email')}
          disabled={rights.readOnlyFull || !rights.financedAssetClientWrite}
        />
      </Grid>

      {!rights.isImage && (
        <Grid item xs={12}>
          <Button
            id={'offerAssetClientCard_pjPartner_saveBtn'}
            color='primary'
            variant='contained'
            onClick={onSave}
            disabled={saving || !isValid || rights.readOnlyFull || !rights.financedAssetClientWrite || !isDirty || !isClientLoaded}
          >
            {saving ? t('Saving') : t('Save')}
          </Button>
        </Grid>
      )}
    </Grid>
  );
};

FacilityFrameOfferAssetClientComponent.propTypes = {
  t: PropTypes.func.isRequired,
  client: PropTypes.object.isRequired,
  isDirty: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onPropertyChange: PropTypes.func.isRequired,
  onUniqueIdBlur: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
  validationInfo: PropTypes.object.isRequired,
  rights: PropTypes.object.isRequired,
  partnerTypeCode: PropTypes.string.isRequired,
  loadLegalPartners: PropTypes.func.isRequired,
  partnerTypeId: PropTypes.number.isRequired,
  isPJ: PropTypes.bool.isRequired,
  setIsPartnerFromModal: PropTypes.func.isRequired,
  isClientLoaded: PropTypes.bool.isRequired,
  onCustomClick: PropTypes.func.isRequired
};

export default compose(withTranslation())(withRights(FacilityFrameOfferAssetClientComponent));
